(function () {
  const TIME_ZONE = "Europe/Amsterdam";
  const SELECTOR = "[data-product-dispatch-incentive]";

  const translations = {
    de: {
      today: "Bestelle innerhalb von <strong>{countdown}</strong> – voraussichtlicher Versand <strong>heute</strong>",
      date: "Bestelle jetzt – voraussichtlicher Versand am <strong>{date}</strong>",
    },
    en: {
      today: "Order within <strong>{countdown}</strong> — expected to ship <strong>today</strong>",
      date: "Order now — expected to ship <strong>{date}</strong>",
    },
    es: {
      today: "Haz tu pedido en <strong>{countdown}</strong> — envío previsto <strong>hoy</strong>",
      date: "Haz tu pedido ahora — envío previsto el <strong>{date}</strong>",
    },
    fr: {
      today: "Commandez dans les <strong>{countdown}</strong> — expédition prévue <strong>aujourd’hui</strong>",
      date: "Commandez maintenant — expédition prévue le <strong>{date}</strong>",
    },
    it: {
      today: "Ordina entro <strong>{countdown}</strong> — spedizione prevista <strong>oggi</strong>",
      date: "Ordina ora — spedizione prevista il <strong>{date}</strong>",
    },
    nl: {
      today: "Bestel binnen <strong>{countdown}</strong> — naar verwachting <strong>vandaag</strong> verzonden",
      date: "Bestel nu — naar verwachting verzonden op <strong>{date}</strong>",
    },
  };

  const dateLocales = {
    de: "de-DE",
    en: "en-GB",
    es: "es-ES",
    fr: "fr-FR",
    it: "it-IT",
    nl: "nl-NL",
  };

  function getAmsterdamParts(date) {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    const parts = Object.fromEntries(
      formatter
        .formatToParts(date)
        .filter((part) => part.type !== "literal")
        .map((part) => [part.type, Number.parseInt(part.value, 10)])
    );

    return parts;
  }

  function createDate(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day));
  }

  function addDays(date, amount) {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + amount);
    return result;
  }

  function dateKey(date) {
    return [
      date.getUTCFullYear(),
      String(date.getUTCMonth() + 1).padStart(2, "0"),
      String(date.getUTCDate()).padStart(2, "0"),
    ].join("-");
  }

  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return createDate(year, month, day);
  }

  function dutchPublicHolidays(year) {
    const easter = easterSunday(year);
    let kingsDay = createDate(year, 4, 27);

    if (kingsDay.getUTCDay() === 0) {
      kingsDay = createDate(year, 4, 26);
    }

    return new Set([
      dateKey(createDate(year, 1, 1)),
      dateKey(addDays(easter, -2)),
      dateKey(addDays(easter, 1)),
      dateKey(kingsDay),
      dateKey(createDate(year, 5, 5)),
      dateKey(addDays(easter, 39)),
      dateKey(addDays(easter, 50)),
      dateKey(createDate(year, 12, 25)),
      dateKey(createDate(year, 12, 26)),
    ]);
  }

  function parseClosureDates(value) {
    return new Set(
      (value || "")
        .split(/[\s,;]+/)
        .filter((entry) => /^\d{4}-\d{2}-\d{2}$/.test(entry))
    );
  }

  function isBusinessDay(date, closureDates) {
    const weekday = date.getUTCDay();
    const key = dateKey(date);

    if (weekday === 0 || weekday === 6 || closureDates.has(key)) return false;

    return !dutchPublicHolidays(date.getUTCFullYear()).has(key);
  }

  function parseCutoff(value) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value || "");
    if (!match) return { hour: 15, minute: 0 };

    const hour = Number.parseInt(match[1], 10);
    const minute = Number.parseInt(match[2], 10);

    if (hour > 23 || minute > 59) return { hour: 15, minute: 0 };

    return { hour, minute };
  }

  function getDispatchEstimate(now, cutoff, closureDates) {
    const parts = getAmsterdamParts(now);
    const today = createDate(parts.year, parts.month, parts.day);
    const secondsNow = parts.hour * 3600 + parts.minute * 60 + parts.second;
    const cutoffSeconds = cutoff.hour * 3600 + cutoff.minute * 60;
    const shipsToday = isBusinessDay(today, closureDates) && secondsNow < cutoffSeconds;

    if (shipsToday) {
      return { date: today, secondsRemaining: cutoffSeconds - secondsNow, today: true };
    }

    let dispatchDate = addDays(today, 1);
    while (!isBusinessDay(dispatchDate, closureDates)) {
      dispatchDate = addDays(dispatchDate, 1);
    }

    return { date: dispatchDate, secondsRemaining: 0, today: false };
  }

  function formatCountdown(seconds) {
    const totalMinutes = Math.max(1, Math.ceil(seconds / 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return hours ? `${hours}h ${String(minutes).padStart(2, "0")}m` : `${minutes}m`;
  }

  function formatDate(date, locale) {
    return new Intl.DateTimeFormat(dateLocales[locale] || dateLocales.en, {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    }).format(date);
  }

  function init(node) {
    if (node.dataset.dispatchInitialized === "true") return;
    node.dataset.dispatchInitialized = "true";

    const locale = translations[node.dataset.locale] ? node.dataset.locale : "en";
    const messages = translations[locale];
    const message = node.querySelector("[data-product-dispatch-message]");
    const cutoff = parseCutoff(node.dataset.cutoffTime);
    const closureDates = parseClosureDates(node.dataset.closureDates);
    const eligibleVariantIds = node.dataset.eligibleVariantIds || ",";
    const productContainer = node.closest(".product__top") || document;
    const variantSelect = productContainer.querySelector("[data-variant-select]");
    const productForm = variantSelect ? variantSelect.closest("form") : null;

    function updateEligibility() {
      const variantId = variantSelect ? variantSelect.value : node.dataset.currentVariantId;
      node.hidden = !eligibleVariantIds.includes(`,${variantId},`);
    }

    function render() {
      const estimate = getDispatchEstimate(new Date(), cutoff, closureDates);
      const html = estimate.today
        ? messages.today.replace("{countdown}", formatCountdown(estimate.secondsRemaining))
        : messages.date.replace("{date}", formatDate(estimate.date, locale));

      message.innerHTML = html;
    }

    (productForm || productContainer).addEventListener("change", () => {
      window.setTimeout(updateEligibility, 0);
    });

    updateEligibility();
    render();
    const intervalId = window.setInterval(() => {
      if (!node.isConnected) {
        window.clearInterval(intervalId);
        return;
      }

      render();
    }, 30000);
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }
})();
